import { useMemo } from 'react';
import { MRT_ColumnDef, MRT_PaginationState } from 'material-react-table';
import { Chip, Grid, IconButton, Tooltip } from '@mui/material';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';

import { GeneralTable } from '@/shared/components/ui';
import { cellNotFilter, formatDateWithTimeCell } from '@/shared';
import { ITicketMasivo } from '@/shared/interfaces/erp/tickets-soporte-clientes/gestion-ticket.interface';
import { useAuthStore } from '@/store/auth/auth.store';
import { useFetchUsuarioResumen } from '@/shared/actions/erp/tickets-soporte-cliente';

const ESTADO_CHIP_COLOR: Record<
  string,
  'error' | 'warning' | 'success' | 'default'
> = {
  activo: 'error',
  pausado: 'warning',
  en_reparacion: 'warning',
  resuelto: 'success',
};

const ESTADO_LABEL: Record<string, string> = {
  activo: 'Activo',
  pausado: 'Pausado',
  en_reparacion: 'En Reparación',
  resuelto: 'Resuelto',
};

const NIVEL_LABEL: Record<string, string> = {
  olt: 'OLT',
  troncal: 'Troncal',
  nap: 'NAP',
  router: 'Router',
};

const elementosCountMasivo = (m: ITicketMasivo): number => {
  const pick = (list?: string[], singular?: string | null): number => {
    const set = new Set<string>();
    if (Array.isArray(list)) list.forEach((x) => { if (x) set.add(x); });
    if (singular) set.add(singular);
    return set.size;
  };
  switch (m.nivel_afectacion) {
    case 'olt': return pick(m.olts_afectadas, m.olt_afectada);
    case 'troncal': return pick(m.troncales_afectadas, m.troncal_afectada);
    case 'nap': return pick(m.naps_afectadas);
    case 'router': return pick(m.routers_afectados, m.router_afectado);
    default: return 0;
  }
};

export type TablaMasivosProps = {
  data: ITicketMasivo[];
  isLoading: boolean;
  isRefetching: boolean;
  pagination: MRT_PaginationState;
  onPaging: (pagination: MRT_PaginationState) => void;
  rowCount?: number;
  onViewDetail: (masivo: ITicketMasivo) => void;
  onResolve: (masivo: ITicketMasivo) => void;
  onPause?: (masivo: ITicketMasivo) => void;
  onReactivate?: (masivo: ITicketMasivo) => void;
};

const TablaMasivos: React.FC<TablaMasivosProps> = ({
  data,
  isLoading,
  isRefetching,
  pagination,
  onPaging,
  rowCount,
  onViewDetail,
  onResolve,
  onPause,
  onReactivate,
}) => {
  // Permisos: cada accion del row se oculta si el usuario no tiene el
  // permiso correspondiente (migracion V1_12_0). El paraguas
  // tickets.gestionar_masivos sigue existiendo pero ahora se desgrana en
  // ver_detalle / pausar / reactivar / resolver para control fino.
  const user = useAuthStore((s) => s.user);
  const isSuperUser = !!(user as any)?.is_superuser;
  const currentUserId = (user as any)?.user_id || (user as any)?.user || 0;
  const { data: _resumenRes } = useFetchUsuarioResumen(currentUserId, !!currentUserId);
  const _r = _resumenRes as any;
  const userPermisos: string[] =
    _r?.data?.data?.permisos || _r?.data?.permisos || _r?.permisos || [];
  // Retrocompat: si el usuario no tiene ningun permiso del modulo 'masivos.'
  // (migracion no aplicada o rol viejo) caemos al permiso paraguas
  // tickets.gestionar_masivos para no romper la UI.
  const tieneAlgunoMasivos = userPermisos.some((p) => p.startsWith('masivos.'));
  const canViaParaguas = userPermisos.includes('tickets.gestionar_masivos');
  const can = (perm: string) =>
    isSuperUser
    || (!tieneAlgunoMasivos && canViaParaguas)
    || userPermisos.includes(perm);

  const canVerDetalle = can('masivos.ver_detalle');
  const canPausar = can('masivos.pausar');
  const canReactivar = can('masivos.reactivar');
  const canResolver = can('masivos.resolver');
  const columns = useMemo<MRT_ColumnDef<ITicketMasivo>[]>(
    () => [
      {
        accessorKey: 'codigo',
        header: 'Código',
        size: 150,
      },
      {
        accessorKey: 'descripcion',
        header: 'Descripción',
        size: 300,
      },
      {
        accessorKey: 'estado_masivo',
        header: 'Estado',
        size: 160,
        Cell: ({ row }) => {
          const estado = row.original.estado_masivo;
          return (
            <Chip
              label={ESTADO_LABEL[estado] ?? estado}
              color={ESTADO_CHIP_COLOR[estado] ?? 'default'}
              size="small"
            />
          );
        },
      },
      {
        accessorKey: 'clientes_afectados_count',
        header: 'Clientes Afectados',
        size: 170,
      },
      {
        accessorKey: 'tiene_afectacion',
        header: 'Afectación',
        size: 130,
        Cell: ({ row }) => {
          // Default true cuando el campo viene undefined (masivos legacy
          // creados antes del flag — los tratabamos como "con afectacion").
          const tiene = row.original.tiene_afectacion !== false;
          return (
            <Chip
              label={tiene ? 'Sí afecta' : 'Sin afectación'}
              color={tiene ? 'error' : 'default'}
              size="small"
              variant={tiene ? 'filled' : 'outlined'}
            />
          );
        },
      },
      {
        ...cellNotFilter,
        accessorKey: 'nivel_afectacion',
        header: 'Nivel',
        size: 170,
        Cell: ({ row }) => {
          const nivel = row.original.nivel_afectacion;
          const count = elementosCountMasivo(row.original);
          const label = `${NIVEL_LABEL[nivel] ?? nivel}${count > 1 ? ` (${count})` : ''}`;
          return (
            <Chip label={label} size="small" variant="outlined" color="primary" />
          );
        },
      },
      {
        ...cellNotFilter,
        accessorKey: 'fecha_creacion',
        header: 'Fecha Creación',
        size: 200,
        Cell: ({ row }) => formatDateWithTimeCell(row, 'fecha_creacion'),
      },
    ],
    [],
  );

  return (
    <GeneralTable<ITicketMasivo>
      columns={columns}
      data={data}
      isLoading={isLoading}
      isRefetching={isRefetching}
      pagination={pagination}
      onPaging={onPaging}
      rowCount={rowCount}
      canEdit={false}
      canDelete={false}
      enableGlobalFilter={false}
      showCustomActionBtnSpace
      customActionBtnSpace={(original) => {
        const isResuelto = original.estado_masivo === 'resuelto';
        const isPausado = original.estado_masivo === 'pausado';
        return (
          <Grid container spacing={1}>
            {canVerDetalle && (
              <Grid item>
                <Tooltip title="Ver Detalle">
                  <IconButton onClick={() => onViewDetail(original)}>
                    <RemoveRedEyeIcon color="info" />
                  </IconButton>
                </Tooltip>
              </Grid>
            )}
            {canPausar && !isResuelto && !isPausado && onPause && (
              <Grid item>
                <Tooltip title="Pausar Masivo">
                  <IconButton onClick={() => onPause(original)}>
                    <PauseCircleIcon color="warning" />
                  </IconButton>
                </Tooltip>
              </Grid>
            )}
            {canReactivar && isPausado && onReactivate && (
              <Grid item>
                <Tooltip title="Reactivar Masivo">
                  <IconButton onClick={() => onReactivate(original)}>
                    <PlayCircleIcon color="success" />
                  </IconButton>
                </Tooltip>
              </Grid>
            )}
            {canResolver && !isResuelto && (
              <Grid item>
                <Tooltip title="Resolver Masivo">
                  <IconButton onClick={() => onResolve(original)}>
                    <CheckCircleIcon color="success" />
                  </IconButton>
                </Tooltip>
              </Grid>
            )}
          </Grid>
        );
      }}
    />
  );
};

export default TablaMasivos;
